import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Trophy, Star, X, Zap } from 'lucide-react';

// ─── Web Audio Fanfare ────────────────────────────────────────────────────────
const playVictorySound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();

    const note = (freq, start, dur, vol = 0.28, type = 'triangle') => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.08);
    };

    // Aufsteigende Fanfare (C-Dur)
    note(523.25, 0.00, 0.18);          // C5
    note(659.25, 0.14, 0.18);          // E5
    note(783.99, 0.28, 0.18);          // G5
    note(1046.50, 0.42, 0.75);         // C6 lang
    note(1318.51, 0.44, 0.55, 0.18);   // E6 harmonisch
    note(1567.98, 0.46, 0.45, 0.12);   // G6 harmonisch

    // Glitzer-Töne
    [1047, 1175, 1319, 1480, 1568].forEach((f, i) =>
      note(f, 0.70 + i * 0.07, 0.12, 0.08, 'sine')
    );

    setTimeout(() => { try { ctx.close(); } catch {} }, 3500);
  } catch {}
};

// ─── Partikel-Generator ───────────────────────────────────────────────────────
const COLORS = [
  '#f59e0b','#10b981','#3b82f6','#ef4444','#8b5cf6',
  '#f97316','#ec4899','#06b6d4','#fbbf24','#34d399','#a78bfa',
];

const makeBurst = (delay = 0) => {
  const x = 8 + Math.random() * 84;
  const y = 5 + Math.random() * 65;
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const count = 14 + Math.floor(Math.random() * 6);
  return Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
    const dist  = 55 + Math.random() * 90;
    return {
      id:    `${Date.now()}-${delay}-${i}`,
      x, y,
      dx:    Math.cos(angle) * dist,
      dy:    Math.sin(angle) * dist,
      color,
      delay,
      size:  3 + Math.random() * 6,
      dur:   0.65 + Math.random() * 0.55,
      rect:  Math.random() > 0.55,
    };
  });
};

const generateWave = (burstCount = 7, baseDelay = 0) =>
  Array.from({ length: burstCount }, (_, b) =>
    makeBurst(baseDelay + b * 0.22)
  ).flat();

// ─── Component ────────────────────────────────────────────────────────────────
export default function CelebrationModal({ open, onClose, goalTitle, onNavigateTo }) {
  const [particles, setParticles] = useState([]);

  const spawnWave = useCallback((base = 0) => {
    setParticles(p => [...p, ...generateWave(7, base)]);
  }, []);

  useEffect(() => {
    if (!open) { setParticles([]); return; }

    playVictorySound();
    spawnWave(0);

    const t1 = setTimeout(() => spawnWave(0),   1500);
    const t2 = setTimeout(() => spawnWave(0),   3000);
    const t3 = setTimeout(() => setParticles([]), 5500);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open, spawnWave]);

  if (!open) return null;

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, background: 'rgba(2,6,23,0.88)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      {/* ── CSS-Keyframes ── */}
      <style>{`
        @keyframes fw-dot {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          70%  { opacity: .85; }
          100% { transform: translate(var(--dx),var(--dy)) scale(0); opacity: 0; }
        }
        @keyframes fw-rect {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--dx),var(--dy)) scale(0) rotate(420deg); opacity: 0; }
        }
        @keyframes trophy-bounce {
          0%,100% { transform: scale(1) rotate(-4deg); }
          50%      { transform: scale(1.12) rotate(4deg); }
        }
        @keyframes card-glow {
          0%,100% { box-shadow: 0 0 24px #10b98155, 0 0 60px #10b98122; }
          50%      { box-shadow: 0 0 48px #10b98199, 0 0 100px #10b98144; }
        }
        @keyframes shimmer {
          0%   { background-position: -300% center; }
          100% { background-position:  300% center; }
        }
        @keyframes star-orbit {
          0%   { transform: rotate(0deg) scale(1); }
          50%  { transform: rotate(180deg) scale(1.3); }
          100% { transform: rotate(360deg) scale(1); }
        }
        @keyframes zap-flash {
          0%,100% { opacity: 1; }
          50%      { opacity: .4; }
        }
        @keyframes slide-up {
          0%   { transform: translateY(40px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      {/* ── Feuerwerk-Partikel ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map(p => (
          <div
            key={p.id}
            className="absolute"
            style={{
              left: `${p.x}%`,
              top:  `${p.y}%`,
              width:        p.size,
              height:       p.rect ? p.size * 0.35 : p.size,
              background:   p.color,
              borderRadius: p.rect ? '1px' : '50%',
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              animation: `${p.rect ? 'fw-rect' : 'fw-dot'} ${p.dur}s ease-out ${p.delay}s both`,
              boxShadow: `0 0 ${p.size}px ${p.color}88`,
            }}
          />
        ))}
      </div>

      {/* ── Karte ── */}
      <div
        className="relative rounded-3xl p-7 mx-4 max-w-sm w-full text-center border border-emerald-500/25"
        style={{
          background: 'linear-gradient(160deg,#0f172a 0%,#0c1f14 60%,#0f172a 100%)',
          animation: 'card-glow 2.5s ease-in-out infinite, slide-up .45s ease-out both',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* X */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all"
        >
          <X size={16} />
        </button>

        {/* Blitz-Icons */}
        <div className="flex justify-between px-2 mb-2">
          {[0,.2,.4].map(d => (
            <Zap key={d} size={16} className="text-yellow-400"
              style={{ animation: `zap-flash 1.2s ease-in-out ${d}s infinite`, fill: '#facc15' }} />
          ))}
        </div>

        {/* Pokal */}
        <div style={{ animation: 'trophy-bounce 1.1s ease-in-out infinite' }} className="mb-4">
          <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706, #b45309)',
              boxShadow: '0 0 30px #f59e0b88, 0 8px 32px #00000066',
            }}>
            <Trophy size={46} className="text-white drop-shadow-lg" />
          </div>
        </div>

        {/* Sterne */}
        <div className="flex justify-center gap-3 mb-3">
          {[0,.15,.30].map(d => (
            <Star key={d} size={22} className="text-yellow-400 fill-yellow-400"
              style={{ animation: `star-orbit 1.8s ease-in-out ${d}s infinite` }} />
          ))}
        </div>

        {/* Titel */}
        <h2 className="text-3xl font-black tracking-tight text-white mb-1">GLÜCKWUNSCH!</h2>
        <p className="text-emerald-400 text-sm font-medium mb-4">Du hast dein Ziel erreicht!</p>

        {goalTitle && (
          <p className="text-slate-300 text-sm mb-5 px-2 font-medium leading-snug">"{goalTitle}"</p>
        )}

        {/* 100% Badge */}
        <div className="rounded-2xl p-4 mb-6 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#064e3b,#065f46,#047857)' }}>
          <div className="text-5xl font-black mb-1" style={{
            background: 'linear-gradient(90deg,#34d399,#6ee7b7,#a7f3d0,#6ee7b7,#34d399)',
            backgroundSize: '300% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmer 2.5s linear infinite',
          }}>
            100% EXP
          </div>
          <p className="text-emerald-300 text-xs">Alle Ziele abgeschlossen!</p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          {onNavigateTo && (
            <button
              onClick={() => { onClose(); onNavigateTo('goals'); }}
              className="w-full py-3.5 rounded-xl font-bold text-white text-base active:scale-95 transition-transform"
              style={{
                background: 'linear-gradient(135deg,#10b981,#059669,#047857)',
                boxShadow: '0 4px 20px #10b98166',
              }}
            >
              Neues Ziel planen
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl font-semibold text-slate-300 text-sm active:scale-95 transition-transform border border-slate-700 hover:border-slate-500"
          >
            Weiter so! 🚀
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
